import { Box } from "rebass/styled-components";
import styled from "styled-components";

/**
 * Player frame.
 *
 * 5px margin all around so the video doesn't butt against the iframe
 * edge or the playlist column — gives the rounded corners room to read
 * as a card, not a flush-bleed video. Border-radius matches the
 * playlist row treatment (8px) so the player and rail rows share a
 * single corner language.
 *
 * The aspect-ratio constraint lives HERE (not on the outer PlayerRow)
 * because the 5px margin gutters distort whatever aspect ratio sits on
 * the outer box: a 16:9 PlayerRow at 226h gives the inner content area
 * (226-10) × (402-10) = 216 × 392, ratio ~1.815, which made the 16:9
 * video pillarbox itself with thin black bars. Anchoring 16:9 on the
 * frame ensures the actual video surface is exactly 16:9, and the
 * outer PlayerRow auto-sizes its width to (frame width + 10px margin).
 *
 * Desktop: height is the anchor (calc(100% - 10px) of the fixed iframe
 *          height), width is derived from aspect-ratio.
 * Mobile:  width is the anchor (full bleed minus the 10px margin),
 *          height is derived from aspect-ratio so the player + rail
 *          stack cleanly.
 *
 * `overflow: hidden` clips the <video> to the rounded shape; without
 * it the video element's rectangular bounds would poke through the
 * corners.
 */
export const PlayerFrame = styled(Box)<{ $bottomPlaylist?: boolean }>`
  aspect-ratio: 16 / 9;
  background: #000;
  border-radius: 8px;
  height: calc(100% - 10px);
  margin: 5px;
  overflow: hidden;
  position: relative;

  /* Bottom-playlist variant: player fills the full iframe width (rail
     moves underneath) so we anchor on width, derive height from the
     16:9 ratio, and keep the horizontal gutter at 14px so the larger
     video doesn't feel jammed against the iframe edge. Publisher
     feedback was that the side-rail player feels claustrophobic — the
     extra horizontal gutter here is what "looks less too big" reads as
     visually. Vertical margins are kept tight (8px top / 4px bottom)
     so the playlist strip below gets a usable height — at iframe
     heights around 520-540, every extra px of player gutter is one
     less px of playable card area. Border-radius keeps the same 8px
     corner language as the side-rail variant, just at a bigger frame. */
  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    height: auto;
    width: calc(100% - 28px);
    margin: 8px 14px 4px;
    border-radius: 10px;
  `}

  @media (max-width: 560px) {
    height: auto;
    width: calc(100% - 10px);
  }
`;

export const PlayerVideo = styled.video`
  display: block;
  width: 100%;
  height: 100%;
  /* Show the entire video frame inside the player area, never crop. If
     the iframe's aspect ratio doesn't match the clip's native ratio
     (e.g. 21:9 iframe with a 16:9 source), <PlayerFrame>'s black
     background fills the gap as letterbox/pillarbox bars rather than
     hiding any part of the gameplay. */
  object-fit: contain;
`;

export const SponsorAnchor = styled(Box)<{
  $position: "ul" | "lr";
  $fs?: boolean;
  $bp?: boolean;
}>`
  position: absolute;
  z-index: 4;
  /* Upper-left watermark is tucked tight to the top-left corner so it
     reads as a small embed signature, not a hovering label. Lower-right
     stays at 12/12. Bottom-playlist nudges the UL anchor inboard a few
     more px since the wordmark itself is larger (110 vs 78) — at the
     base 6/6 inset, the bigger mark would crowd the corner radius. */
  ${({ $position, $bp }) =>
    $position === "ul"
      ? $bp
        ? "top: 10px; left: 12px;"
        : "top: 6px; left: 6px;"
      : "bottom: 12px; right: 12px;"}
  pointer-events: auto;
  /* Fullscreen: the player covers the whole viewport, so the small
     embed-scale watermark would look like a sticker. Scale up
     dramatically so it reads at TV/desktop-fullscreen distance.
     transform-origin anchors to the same corner the watermark sits in
     so the scaled-up logo doesn't drift away from the edge. */
  transform: ${({ $fs }) => ($fs ? "scale(2.6)" : "none")};
  transform-origin: ${({ $position }) =>
    $position === "ul" ? "top left" : "bottom right"};
  transition: transform 200ms ease;
`;

export const SponsorLogoLink = styled.a`
  display: inline-block;
  cursor: pointer;
  /* Subtle drop shadow so logos read on bright video. */
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.6));
  transition: transform 120ms ease;

  &:hover {
    transform: scale(1.04);
  }
`;
